import { eventDateKeys } from '@trackmind/core';
/**
 * Read-only Google Calendar client.
 *
 * There is no write method here, on purpose: connecting a work account must
 * not be able to change anything in it. If two-way sync is ever added it
 * belongs in a separate class with a separate scope and separate consent.
 *
 * Refresh is incremental. Google hands back a `nextSyncToken` on a full
 * listing; passing it next time returns only what changed since — typically
 * an empty response, so polling several calendars every few minutes costs
 * almost nothing. Tokens expire (410 GONE), which is not an error but an
 * instruction to re-list the window from scratch.
 */
const CALENDAR_API = 'https://www.googleapis.com/calendar/v3';
export class GoogleCalendarAPI {
    getAccessToken;
    constructor(config) {
        this.getAccessToken = config.getAccessToken;
    }
    async request(path, params) {
        const url = new URL(`${CALENDAR_API}${path}`);
        for (const [key, value] of Object.entries(params)) {
            if (value !== undefined)
                url.searchParams.set(key, value);
        }
        const send = async (token) => fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
        let token = await this.getAccessToken();
        let response = await send(token);
        // One retry on 401: the token may have been revoked between refresh and
        // use. getAccessToken() throws if the grant is genuinely dead.
        if (response.status === 401) {
            token = await this.getAccessToken();
            response = await send(token);
        }
        if (response.status === 410) {
            const error = new Error('Sync token expired');
            error.code = 410;
            throw error;
        }
        if (!response.ok) {
            throw new Error(`Calendar API ${response.status}: ${await response.text()}`);
        }
        return response.json();
    }
    /** Every calendar this account can read. */
    async listCalendars() {
        const entries = [];
        let pageToken;
        do {
            const data = await this.request('/users/me/calendarList', {
                maxResults: '250',
                showHidden: 'false',
                pageToken,
            });
            for (const item of data.items ?? []) {
                if (item.deleted)
                    continue;
                entries.push(item);
            }
            pageToken = data.nextPageToken;
        } while (pageToken);
        return entries;
    }
    /**
     * Fetch events for one calendar.
     *
     * With `syncToken`, returns only changes since that token. Without one (or
     * when the token has expired), lists the whole `[timeMin, timeMax]` window.
     * `singleEvents` expands recurring series into concrete instances, which is
     * what a schedule needs — an unexpanded weekly standup would otherwise be a
     * single event with an RRULE we'd have to interpret ourselves.
     */
    async listEvents(options) {
        const { calendarId, sourceId, accountId, timeMin, timeMax } = options;
        const fetchPages = async (syncToken) => {
            const events = [];
            const touchedBaseIds = [];
            let pageToken;
            let nextSyncToken = null;
            do {
                // timeMin/timeMax are illegal alongside syncToken — Google rejects the
                // combination outright rather than ignoring it.
                const data = await this.request(`/calendars/${encodeURIComponent(calendarId)}/events`, {
                    maxResults: '2500',
                    singleEvents: 'true',
                    showDeleted: syncToken ? 'true' : 'false',
                    pageToken,
                    ...(syncToken
                        ? { syncToken }
                        : { timeMin, timeMax, orderBy: 'startTime' }),
                });
                for (const item of data.items ?? []) {
                    touchedBaseIds.push(`${sourceId}::${item.id}`);
                    if (item.status === 'cancelled')
                        continue;
                    const mapped = mapEvent(item, sourceId, accountId);
                    if (mapped)
                        events.push(...mapped);
                }
                pageToken = data.nextPageToken;
                nextSyncToken = data.nextSyncToken ?? nextSyncToken;
            } while (pageToken);
            return { events, touchedBaseIds, syncToken: nextSyncToken, didFullResync: syncToken === null };
        };
        if (options.syncToken) {
            try {
                return await fetchPages(options.syncToken);
            }
            catch (error) {
                // 410 GONE just means "your cursor is too old" — re-list the window.
                if (error.code !== 410)
                    throw error;
            }
        }
        return fetchPages(null);
    }
}
function mapResponse(item) {
    const self = (item.attendees ?? []).find((a) => a.self);
    if (self?.responseStatus)
        return self.responseStatus;
    return 'none';
}
/**
 * One provider event becomes one cache row per local day it touches, so the
 * cache can be read by day without scanning everything. A meeting that crosses
 * midnight legitimately belongs to both days.
 */
function mapEvent(item, sourceId, accountId) {
    const allDay = Boolean(item.start?.date);
    const start = item.start?.dateTime ?? item.start?.date;
    const end = item.end?.dateTime ?? item.end?.date ?? start;
    if (!start)
        return null;
    const base = {
        source_id: sourceId,
        account_id: accountId,
        title: item.summary ?? '(no title)',
        start,
        end,
        all_day: allDay,
        location: item.location ?? null,
        organizer: item.organizer?.email ?? null,
        response: mapResponse(item),
        html_link: item.htmlLink ?? null,
        updated_at: item.updated ?? new Date().toISOString(),
    };
    const days = eventDateKeys(start, end, allDay);
    return days.map((date, index) => ({
        ...base,
        // Continuation rows need distinct keys, or day 2 would overwrite day 1.
        id: index === 0 ? `${sourceId}::${item.id}` : `${sourceId}::${item.id}::${date}`,
        date,
    }));
}
//# sourceMappingURL=google-calendar.js.map